import React, { useEffect, useState } from 'react';
import Layout from '../layout/Layout';
import PageTransition from '../components/PageTransition';
import UsageChart from '../components/UsageChart'; 
import { getAdminDashboard as getAdminMasterStats } from '../components/DbServices';
import { NavLink } from 'react-router-dom';
import Swal from 'sweetalert2';

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAdminData = async () => {
            try {
                const res = await getAdminMasterStats();
                if (res?.status === 200) setData(res.data);
            } catch (err) {
                console.error("Dashboard Sync Error", err);
            } finally {
                setLoading(false);
            }
        };
        fetchAdminData();
    }, []);

    if (loading) return <AdminLoader />;

    return (
        <Layout>
            <PageTransition>
                <div className="container-fluid py-4">
                    
                    {/* HEADER SECTION */}
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h3 className="fw-black mb-1" style={{ color: '#1e293b' }}>
                                System <span className="text-primary" style={{ filter: 'brightness(1.2)' }}>Intelligence</span> 🛡️
                            </h3>
                            <p className="text-muted small fw-medium">Global monitor for users, revenue, and API health.</p>
                        </div>
                        <div className="d-flex gap-2">
                            <NavLink to="/admin/plans" className="btn btn-primary rounded-pill shadow px-4 fw-bold btn-sm border-0" style={{ background: 'linear-gradient(45deg, #4f46e5, #06b6d4)' }}>
                                <i className="fas fa-layer-group me-2"></i> Manage Plans
                            </NavLink>
                        </div>
                    </div>

                    {/* TOP METRIC CARDS - Colorful & High Contrast */}
                    <div className="row g-3 mb-4">
                        <AdminStatCard title="Net Revenue" value={`Rs. ${data.summary.finance.total_revenue || '0.00'}`} icon="fa-wallet" color="#10b981" bg="#ecfdf5" trend="Lifetime Earnings" />
                        <AdminStatCard title="Total Users" value={data.summary.users.total_users} icon="fa-users" color="#6366f1" bg="#eef2ff" trend={`${data.summary.users.new_users_today} joined today`} />
                        <AdminStatCard title="Active Subs" value={data.summary.finance.total_active_subs} icon="fa-crown" color="#f59e0b" bg="#fffbeb" trend="Premium Accounts" />
                        <AdminStatCard title="Total Traffic" value={data.summary.messages_total} icon="fa-paper-plane" color="#06b6d4" bg="#ecfeff" trend="Messages Delivered" />
                    </div>

                    <div className="row g-4">
                        {/* CHART SECTION */}
                        <div className="col-lg-8">
                            <div className="card border-0 shadow-sm rounded-4 h-100 p-4 bg-white border-top border-primary border-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <h6 className="fw-bold mb-0 text-dark">Revenue Trend (7 Days)</h6>
                                    <span className="badge rounded-pill bg-primary-subtle text-primary px-3">Live Analysis</span>
                                </div>
                                <div style={{ minHeight: '280px' }}>
                                    <UsageChart chartData={data.chart} color="#6366f1" />
                                </div>
                            </div>
                        </div>

                        {/* SYSTEM HEALTH */}
                        <div className="col-lg-4">
                            <div className="card border-0 shadow-sm rounded-4 h-100 p-4 text-white" style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)' }}>
                                <h6 className="fw-bold mb-4 opacity-75 text-uppercase small" style={{ letterSpacing: '1px' }}>Node Connectivity</h6>
                                <div className="text-center py-3">
                                    <h1 className="fw-black mb-0" style={{ fontSize: '3.5rem', color: '#38bdf8' }}>{data.summary.devices.online_count}</h1>
                                    <p className="opacity-50 small fw-medium">Active Instances / {data.summary.devices.total_devices}</p>
                                </div>

                                <div className="progress rounded-pill mb-4" style={{ height: '8px', background: 'rgba(255,255,255,0.1)' }}>
                                    <div className="progress-bar bg-info shadow-sm" style={{ width: `${(data.summary.devices.online_count / data.summary.devices.total_devices) * 100}%` }}></div>
                                </div>

                                <div className="mt-2">
                                    <SystemCheckItem label="Database Cluster" status="Optimal" color="#10b981" />
                                    <SystemCheckItem label="WhatsApp Gateway" status="Active" color="#10b981" pulse={true} />
                                    <SystemCheckItem label="Message Broker" status="Syncing" color="#38bdf8" />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* BOTTOM LOGS */}
                    <div className="row g-4 mt-1">
                        <div className="col-lg-6">
                            <div className="card border-0 shadow-sm rounded-4 overflow-hidden bg-white">
                                <div className="p-4 border-bottom bg-light-subtle">
                                    <h6 className="fw-bold mb-0"><i className="fas fa-chart-pie me-2 text-primary"></i>Plan Distribution</h6>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-hover align-middle mb-0">
                                        <thead className="bg-light">
                                            <tr className="text-muted small text-uppercase fw-bold">
                                                <th className="ps-4 py-3 border-0">Plan</th>
                                                <th className="py-3 border-0">Users</th>
                                                <th className="pe-4 py-3 border-0 text-end">Revenue</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data.plans.map((p, idx) => (
                                                <tr key={idx}>
                                                    <td className="ps-4 py-3 fw-bold text-dark small">{p.plan_name}</td>
                                                    <td className="small text-muted">{p.user_count} Active</td>
                                                    <td className="pe-4 text-end fw-black text-success">Rs. {p.revenue}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div className="col-lg-6">
                            <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                                <h6 className="fw-bold mb-4 text-dark"><i className="fas fa-history me-2 text-warning"></i>Recent Activity</h6>
                                {data.recent.map((log, idx) => (
                                    <div key={idx} className="d-flex align-items-center justify-content-between mb-3 pb-3 border-bottom border-light">
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="rounded-circle d-flex align-items-center justify-content-center shadow-sm" style={{ width: '40px', height: '40px', background: '#f8fafc', color: '#25d366' }}>
                                                <i className="fab fa-whatsapp fs-5"></i>
                                            </div>
                                            <div>
                                                <div className="small fw-bold text-dark mb-0">{log.device_name}</div>
                                                <div className="text-muted x-small">{log.owner_name}</div>
                                            </div>
                                        </div>
                                        <span className={`badge rounded-pill fw-bold ${log.status.includes('connected') ? 'bg-success text-white' : 'bg-danger text-white'} px-3 py-2`} style={{ fontSize: '8px' }}>
                                            {log.status.toUpperCase()}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </PageTransition>
        </Layout>
    );
}

function AdminStatCard({ title, value, icon, color, bg, trend }) {
    return (
        <div className="col-md-3">
            <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100 border-bottom border-4" style={{ borderBottomColor: color + ' !important' }}>
                <div className="d-flex align-items-center mb-3">
                    <div className="rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm" style={{ width: '50px', height: '50px', backgroundColor: bg, color: color }}>
                        <i className={`fas ${icon} fa-lg`}></i>
                    </div>
                    <div>
                        <p className="text-muted small fw-bold text-uppercase mb-0" style={{ fontSize: '9px', letterSpacing: '1px' }}>{title}</p>
                        <h3 className="fw-black mb-0" style={{ color: '#1e293b' }}>{value}</h3>
                    </div>
                </div>
                <div className="small fw-medium text-muted" style={{ fontSize: '10px' }}>
                    <i className="fas fa-long-arrow-alt-up text-success me-1"></i> {trend}
                </div>
            </div>
        </div>
    );
}

function SystemCheckItem({ label, status, color, pulse }) {
    return (
        <div className="d-flex justify-content-between align-items-center mb-3 bg-white bg-opacity-10 p-2 rounded-3">
            <span className="small fw-medium opacity-75">{label}</span>
            <span className="fw-bold" style={{ color: color, fontSize: '10px' }}>
                {pulse && <span className="pulse-dot me-2" style={{ backgroundColor: color }}></span>}
                {status.toUpperCase()}
            </span>
        </div>
    );
}

function AdminLoader() {
    return (
        <div className="vh-100 d-flex flex-column justify-content-center align-items-center" style={{ backgroundColor: '#f8fafc' }}>
            <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }} role="status"></div>
            <h5 className="mt-4 fw-black text-dark text-uppercase tracking-widest">Master Dashboard Syncing...</h5>
        </div>
    );
}